import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { AdminScope, AdminScopeDataIngestionFlags } from '@pubstack/common/src/scope'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, ReactNode } from 'react'
import { Colors } from '~/assets/style/colors'
import { BetaBadge } from '~/components/BetaBadge'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { IconName } from '~/components/Icon'
import { IconWithTooltip } from '~/components/IconWithTooltip'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Spinner } from '~/components/Spinner'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { WithClassName } from '~/types/utils'

const FEATURE_TOGGLE_CLASS = 'inline-flex items-center gap-1.5'

const FeatureSpinner = () => (
  <span className="inline-flex w-11 justify-center">
    <Spinner />
  </span>
)

type FeatureContent = {
  title: string
  icon?: IconName
  feature?: Feature
  onboardingRequired?: boolean
  ingestion?: keyof AdminScopeDataIngestionFlags | ('adxEnabled' | 'openBiddingEnabled' | 'gamIntegrationEnabled')
  additionalFeature?: 'sessionTrackingDisabled'
  additionalFeatureTitle?: string
  isCollapsible?: boolean
  collapsibleContent?: ReactNode
  releaseStage?: 'beta'
  offboardConfirmationMessage?: string
  tooltip?: string
}

type PureAdminScopeFeaturesProps = WithClassName & {
  scope: AdminScope
  isLoading: boolean
  toggleFeature: (feature: Feature) => unknown
  toggleDataIngestion: (flag: keyof AdminScopeDataIngestionFlags) => unknown
  toggleFeatureOnboarding: (feature: Feature) => unknown
  gamCollapsibleContent: ReactNode
  gamIngestion: { adxEnabled: boolean; openBiddingEnabled: boolean; gamIntegrationEnabled: boolean }
}
export const PureAdminScopeFeatures: FunctionComponent<PureAdminScopeFeaturesProps> = ({
  className,
  isLoading,
  scope,
  gamCollapsibleContent,
  toggleFeature,
  toggleDataIngestion,
  toggleFeatureOnboarding,
  gamIngestion,
}) => {
  const dataIngestions: AdminScopeDataIngestionFlags & { adxEnabled: boolean; openBiddingEnabled: boolean; gamIntegrationEnabled: boolean } = {
    ...scope,
    ...gamIngestion,
  }

  const modal = useGlobalModal()

  const FEATURES_CONFIG: {
    title: string
    content: FeatureContent[]
  }[] = [
    {
      title: 'GAM',
      content: [
        {
          title: 'GAM integration & data ingestion',
          isCollapsible: true,
          collapsibleContent: gamCollapsibleContent,
        },
        { title: 'AdX', icon: 'gam_color', feature: 'adx', ingestion: 'adxEnabled' },
        { title: 'Open Bidding', icon: 'gam_color', feature: 'openBidding', ingestion: 'openBiddingEnabled' },
      ],
    },
    {
      title: 'Analytics',
      content: [
        { title: 'Analytics basics (Overview, Bidders, Sites, Reports)', icon: 'chart_bar', feature: 'analytics' },
        { title: 'Viewability', icon: 'view', feature: 'viewability', ingestion: 'viewabilityEnabled' },
        { title: 'Ad refresh (legacy)', icon: 'refresh', feature: 'refresh', ingestion: 'refreshEnabled' },

        {
          title: 'User sessions',
          icon: 'group',
          feature: 'standaloneUserSession',
          ingestion: 'standaloneUserSessionEnabled',
          additionalFeature: 'sessionTrackingDisabled',
          additionalFeatureTitle: 'Disable Session Tracking',
        },
        {
          title: 'Core Web Vitals',
          icon: 'time',
          feature: 'coreWebVitals',
          ingestion: 'coreWebVitalsEnabled',
          releaseStage: 'beta',
        },
        { title: 'A/B test', icon: 'population_split', feature: 'abtest' },
        { title: 'SmartAd Server', icon: 'smart', feature: 'smartAdServer', ingestion: 'smartEnabled' },
        {
          title: 'Service API keys',
          icon: 'key',
          feature: 'serviceApiKeys',
          releaseStage: 'beta',
          tooltip: 'Lets the scope owners create and revoke service API keys, in the "Service keys" tab of the API keys settings.',
        },
        {
          title: 'MCP server',
          icon: 'chat',
          feature: 'mcp',
          releaseStage: 'beta',
          tooltip: 'Lets the scope users query Pubstack from AI assistants (Claude, ...) through the Pubstack MCP server.',
        },
      ],
    },
    {
      title: 'Google ad manager',
      content: [
        { title: 'GAM overview Web', icon: 'shiny_web', feature: 'gamOverviewWeb' },
        { title: 'GAM overview APP', icon: 'shiny_app', feature: 'gamOverviewApp' },
        { title: 'GAM overview AMP', icon: 'bolt', feature: 'gamOverviewAmp' },
      ],
    },
    {
      title: 'Ad Management (Klean)',
      content: [
        { title: 'Ad Management', icon: 'rocket', feature: 'adstack' },
        { title: 'Refresh', icon: 'refresh', feature: 'admRefresh' },
        { title: 'Marketplace teaser', icon: 'fire', feature: 'marketplaceTeaser' },
        { title: 'Marketplace', icon: 'store_front', feature: 'marketplace' },
        { title: 'Instream', icon: 'outstream', feature: 'instream', releaseStage: 'beta' },
        { title: 'Interstitial', icon: 'interstitial', feature: 'interstitial', releaseStage: 'beta' },
      ],
    },
    {
      title: 'Node',
      content: [
        {
          title: 'PBS standalone',
          feature: 'pbsStandalone',
          onboardingRequired: true,
        },
        {
          title: 'Spark',
          feature: 'pbstckBidder',
          onboardingRequired: true,
          offboardConfirmationMessage:
            'The offboarding process will remove the customer\'s Spark seat, all the "*-spark" internal seats, and all related mapping. This change can\'t be undone. Are you sure you want to proceed?',
        },
        {
          title: 'Spark for Network',
          feature: 'sparkForNetwork',
          tooltip: 'When enabled, the Spark bidder seat is hidden from the client in "My bidder seats". Use it for networks whose Spark demand is managed through the SSP allowlist.',
        },
        {
          title: 'Spark for Pub',
          feature: 'sparkForPub',
          onboardingRequired: true,
          tooltip: 'When onboarded, a scope-level spark-for-pub stack is created so the Spark team can map sparkForPub seats by placement.',
        },
        {
          title: 'Spark Demand dashboard',
          icon: 'chart_bar',
          feature: 'sparkDemandDashboard',
          releaseStage: 'beta',
          tooltip: "Shows the scope's users the month-to-date Spark demand revenue, with the billing adjustment set in the Spark Console. Pubstack users see it on every scope onboarded on Spark.",
        },
      ],
    },
  ]

  const isFeatureOnboarded = (scope: AdminScope, featureName: string): boolean => {
    return !!scope.onboardedFeatures?.includes(featureName)
  }

  const isScopeIngestionFlag = (flag: string): flag is keyof AdminScopeDataIngestionFlags =>
    ['viewabilityEnabled', 'refreshEnabled', 'standaloneUserSessionEnabled', 'smartEnabled', 'sessionTrackingDisabled', 'coreWebVitalsEnabled'].includes(flag)

  const handleSetupToggleClick = (featureContent?: FeatureContent) => {
    if (!featureContent || !featureContent.feature) return

    const isOnboarded = isFeatureOnboarded(scope, featureContent.feature)

    if (isOnboarded && featureContent.offboardConfirmationMessage) {
      modal.open(ConfirmationModal, {
        title: 'Warning!',
        message: featureContent.offboardConfirmationMessage,
        trackingLabel: 'common-admin-scopes-detail-features-user-access-deactivation-confirmation',
        confirmVariant: 'negative',
        onCancel: () => modal.close(),
        onConfirm: async () => {
          modal.close()
          featureContent.feature && toggleFeatureOnboarding(featureContent.feature)
        },
      })
    } else {
      toggleFeatureOnboarding(featureContent.feature)
    }
  }

  return (
    <div className={cn('flex flex-col gap-6 pb-6', className)}>
      <WidgetMessage className="self-start" icon={'warning'} message={'Make sure that data are correctly ingested before granting access to users.'} />
      {FEATURES_CONFIG.map((featureConfig, index) => (
        <div key={index} className="flex flex-col gap-2">
          <h2 className="mb-2 font-rubik text-[16px] leading-[26px] font-medium">{featureConfig.title}</h2>
          {featureConfig.content.map((content, contentIndex) => {
            const contentIngestion = content.ingestion //needed for the "isScopeIngestionFlag" type assertion to work
            const contentSetupRequired = content.onboardingRequired
            const additionalFeature = content.additionalFeature
            return (
              <CollapsiblePanel
                key={`feature-${contentIndex}`}
                icon={content.icon}
                title={
                  <span>
                    {content.title}
                    {content.releaseStage === 'beta' && <BetaBadge />}
                    {content.tooltip && (
                      <IconWithTooltip className="relative -top-1.5 ml-1 opacity-80 hover:opacity-100" title={content.tooltip} name="info" fill={Colors.Hurricane} width="1rem" align="center" />
                    )}
                  </span>
                }
                isCollapsible={content.isCollapsible ? { trackingLabel: 'common-admin-scopes-detail-features-gam-collapse-apply' } : undefined}
                actions={
                  <div className="flex gap-[30px]">
                    {contentIngestion && isScopeIngestionFlag(contentIngestion) && (
                      <div className={FEATURE_TOGGLE_CLASS}>
                        <span>Data ingestion</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            value={scope[contentIngestion]}
                            onClick={() => toggleDataIngestion(contentIngestion)}
                            trackingLabel={
                              scope[contentIngestion]
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(contentIngestion)}-ingestion-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(contentIngestion)}-ingestion-set`
                            }
                          />
                        )}
                      </div>
                    )}
                    {additionalFeature && (
                      <div className={FEATURE_TOGGLE_CLASS}>
                        <span>{content.additionalFeatureTitle}</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            disabled={contentIngestion && !dataIngestions[contentIngestion]}
                            value={scope[additionalFeature]}
                            onClick={() => toggleDataIngestion(additionalFeature)}
                            trackingLabel={
                              scope[additionalFeature]
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(additionalFeature)}-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(additionalFeature)}-set`
                            }
                          />
                        )}
                      </div>
                    )}
                    {contentSetupRequired && content.feature && (
                      <div className={FEATURE_TOGGLE_CLASS}>
                        <Tooltip title="This feature requires particular setup for the feature to run. Please toggle the onboarding before using the feature.">
                          <span>{isFeatureOnboarded(scope, content.feature) ? 'Onboarded' : 'Onboarding'}</span>
                        </Tooltip>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            value={isFeatureOnboarded(scope, content.feature)}
                            onClick={() => handleSetupToggleClick(content)}
                            trackingLabel={
                              isFeatureOnboarded(scope, content.feature)
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-setup-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-setup-set`
                            }
                          />
                        )}
                      </div>
                    )}
                    {content.feature && (
                      <div className={FEATURE_TOGGLE_CLASS}>
                        <span>User access</span>
                        {isLoading ? (
                          <FeatureSpinner />
                        ) : (
                          <Toggle
                            disabled={contentIngestion && !dataIngestions[contentIngestion]}
                            value={scope.features?.includes(content.feature)}
                            onClick={() => content.feature && toggleFeature(content.feature)}
                            trackingLabel={
                              scope.features?.includes(content.feature)
                                ? `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-user-access-unset`
                                : `common-admin-scopes-detail-features-${normalizeAsTrackingLabel(content.feature)}-user-access-set`
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                }
              >
                {content.collapsibleContent}
              </CollapsiblePanel>
            )
          })}
        </div>
      ))}
    </div>
  )
}
